import fp from 'lodash/fp'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { ValidationError } from 'todistukset-common/src/vtj/form-validation.util'
import { getIsOrderPending } from 'todistukset-ui/src/vtj/stores/actions/order-actions'
import {
  getVisitedStepsData,
  setAllStepsVisited,
  setCurrentStepIndex,
} from 'todistukset-ui/src/vtj/stores/actions/steps-actions'
import StepRoutes, {
  getStepTitleKey,
} from 'todistukset-ui/src/vtj/todistukset-step-routes.enum'
import OrderFlowName from 'todistukset-ui/src/vtj/OrderFlowName.enum'
import {
  Step,
  StepButtons,
  StepComponent,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'

type OrderFlowNameParam = {
  orderFlowName: OrderFlowName
}

type LanguageParam = {
  lang: LanguageCode
}

type StepRouteParam = {
  step: StepRoutes
}

type StepListParam = {
  steps: Step[]
}

type StepsPublicParams = {
  steps: Step[]
  step: StepRoutes
}

export type StepValidatorParamsParam = {
  stepValidatorParams: Parameters<Step['validations'][0]>[0]
}

const findIndexOfNotVisited = ({
  orderFlowName,
}: OrderFlowNameParam): number => {
  const visits = getVisitedStepsData(orderFlowName)
  const index = fp.findIndex(fp.negate(fp.identity), visits)
  return index === -1 ? visits.length : index
}

export const getStepIndex = ({ steps, step }: StepsPublicParams): number =>
  steps.find((item) => item.name === step)?.index ?? -1

const getStep = ({
  step,
  steps,
}: StepRouteParam & StepListParam): Step | undefined =>
  steps.find((item) => item.name === step)

const getStepPathByOffset = ({
  step,
  steps,
  lang,
  offset,
}: StepRouteParam & StepListParam & LanguageParam & { offset: number }):
  | string
  | null => {
  const stepIndex = getStep({ step, steps })?.index ?? -1
  const nextStep = steps[stepIndex + offset]
  return nextStep && stepIndex >= 0 ? nextStep.getPath(lang) : null
}

export const getNextStepPath = ({
  steps,
  step,
  lang,
}: StepsPublicParams & LanguageParam): string | null =>
  getStepPathByOffset({
    step,
    steps,
    lang,
    offset: 1,
  })

export const getPrevStepPath = ({
  steps,
  step,
  lang,
}: StepsPublicParams & LanguageParam): string | null =>
  getStepPathByOffset({
    step,
    steps,
    lang,
    offset: -1,
  })

type StepValidationResult = {
  isAllowedToProceedTo: boolean
  isAllowedToProceedFrom: boolean
  validationErrors: ValidationError[]
}

const runValidations = ({
  stepValidatorParams,
  steps,
}: StepValidatorParamsParam & StepListParam): StepValidationResult[] => {
  const size = steps.length
  const check = fp.some((validate: Step['validations'][0]) => {
    const result = validate(stepValidatorParams)
    if (typeof result === 'boolean') {
      return !result
    }
    return result.length
  })
  const failureIndex = fp.findIndex((item: Step) => check(item.validations))(
    steps
  )

  return fp.map(
    (index) => ({
      isAllowedToProceedTo: failureIndex === -1 || failureIndex >= index,
      isAllowedToProceedFrom: failureIndex === -1 || failureIndex > index,
      validationErrors: fp.flatMap(
        (validation) => validation(stepValidatorParams),
        steps[index].validations
      ),
    }),
    fp.range(0, size)
  )
}

export const getComponent = ({
  steps,
  step,
}: StepsPublicParams): StepComponent | null =>
  getStep({ step, steps })?.component ?? null

export const getCurrentStepItem = ({
  stepValidatorParams,
  orderFlowName,
  steps,
  step,
  lang,
}: StepsPublicParams &
  StepValidatorParamsParam &
  OrderFlowNameParam &
  LanguageParam): StepItem => {
  const stepIndex = getStepIndex({ steps, step })
  const stepItems = getStepItems({
    orderFlowName,
    stepValidatorParams,
    steps: steps,
    lang,
  })
  return stepItems[stepIndex]
}

export const getDefaultRedirectPath = ({
  steps,
  lang,
}: Pick<StepsPublicParams, 'steps'> & LanguageParam): string =>
  steps[0].getPath(lang)

export const getIsGloballyDisabled = (step?: StepRoutes): boolean =>
  getIsOrderPending() || step === StepRoutes.AET_SIGNER_TODISTUSPYYNTO_REJECTED

export type StepNavigationItem = {
  isEnabled: boolean
  textKey: string
  path: string
}

export const getNavigationItems = ({
  stepValidatorParams,
  orderFlowName,
  steps,
  lang,
}: Pick<StepsPublicParams, 'steps'> &
  StepValidatorParamsParam &
  OrderFlowNameParam &
  LanguageParam): StepNavigationItem[] => {
  const stepItems = getStepItems({
    stepValidatorParams,
    orderFlowName,
    steps,
    lang,
  }).filter((stepItem) => !stepItem.isHidden)
  return fp.map((stepItem: StepItem) => ({
    isEnabled: stepItem.isAllowedToSkipTo,
    textKey: stepItem.textKey,
    path: stepItem.path,
  }))(stepItems)
}

export type StepItem = {
  textKey: string
  onPrevClick: Step['onPrevClick']
  useOnNextClick: Step['useOnNextClick']
  path: string
  isAllowedToProceedTo: boolean
  isAllowedToProceedFrom: boolean
  isAllowedToSkipTo: boolean
  validationErrors: ValidationError[]
  isVisited: boolean
  buttons: StepButtons
  isHidden?: boolean
  useCustomNextButton?: () => StepButtons
}

const getStepItems = ({
  stepValidatorParams,
  orderFlowName,
  steps,
  lang = LanguageCode.FI,
}: StepValidatorParamsParam &
  OrderFlowNameParam &
  StepListParam &
  LanguageParam): StepItem[] => {
  const visitedStepData = getVisitedStepsData(orderFlowName)
  const validationResults = runValidations({ stepValidatorParams, steps })
  type ZippedDataRow = [Step, boolean, StepValidationResult]
  const zippedData = fp
    .zipAll([steps, visitedStepData, validationResults])
    .filter((row): row is ZippedDataRow => row.every((d) => d !== undefined))
  return fp.map(
    ([
      {
        onPrevClick,
        useOnNextClick,
        name,
        buttons,
        getPath,
        isHidden,
        useCustomNextButton,
      },
      isVisited,
      { isAllowedToProceedTo, isAllowedToProceedFrom, validationErrors },
    ]: ZippedDataRow) => ({
      textKey: getStepTitleKey(orderFlowName, name),
      onPrevClick,
      useOnNextClick,
      path: getPath(lang),
      isAllowedToProceedTo,
      isAllowedToProceedFrom,
      isAllowedToSkipTo: isAllowedToProceedTo && isVisited,
      isVisited,
      validationErrors,
      buttons,
      useCustomNextButton,
      isHidden,
    })
  )(zippedData)
}

export const getStepCount = (steps: Step[]): number =>
  steps.filter(({ isHidden }) => !isHidden).length

export const handleLanding = ({
  orderFlowName,
  steps,
  step,
}: StepsPublicParams & OrderFlowNameParam): void => {
  const stepIndex = getStepIndex({ steps, step })
  if (
    [StepRoutes.ORDER_COMPLETE, StepRoutes.UAET_ORDER_COMPLETE].includes(step)
  ) {
    setAllStepsVisited(orderFlowName)
    setCurrentStepIndex(orderFlowName, stepIndex)
  }

  if (step === StepRoutes.AET_SIGNER_TODISTUSPYYNTO_REJECTED) {
    setCurrentStepIndex(orderFlowName, stepIndex)
    return
  }

  const firstNotVisited = findIndexOfNotVisited({ orderFlowName })
  if (stepIndex !== -1 && firstNotVisited >= stepIndex) {
    setCurrentStepIndex(orderFlowName, stepIndex)
  }
}

export const validateLanding = ({
  stepValidatorParams,
  orderFlowName,
  steps,
  step,
}: Omit<StepsPublicParams, 'step'> &
  StepValidatorParamsParam &
  OrderFlowNameParam & {
    step: StepRoutes | undefined
  }): boolean => {
  if (step === undefined) {
    return false
  }

  const item = getStep({ step, steps })

  if (!item) {
    return false
  }

  if (
    [
      StepRoutes.ORDER_COMPLETE,
      StepRoutes.UAET_ORDER_COMPLETE,
      StepRoutes.AET_SIGNER_TODISTUSPYYNTO_REJECTED,
    ].includes(step)
  ) {
    return true
  }

  const stepIndex = getStepIndex({ steps, step })
  const firstNotVisited = findIndexOfNotVisited({ orderFlowName })

  if (firstNotVisited !== -1 && firstNotVisited < stepIndex) {
    return false
  }

  const validations = runValidations({ stepValidatorParams, steps })
  return validations[stepIndex].isAllowedToProceedTo
}
